import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Sparkles, 
  TrendingUp, 
  DollarSign, 
  AlertTriangle, 
  RefreshCw,
  ChevronRight,
  Loader2,
  Lightbulb,
  Zap,
  Target,
  Building2,
  CheckCircle,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  dollar: DollarSign,
  trending: TrendingUp,
  alert: AlertTriangle,
  refresh: RefreshCw,
  lightbulb: Lightbulb,
  target: Target,
  industry: Building2,
  check: CheckCircle,
  warning: AlertTriangle
};

const priorityColors = {
  high: 'bg-red-500/20 text-red-400 border-red-500/30',
  medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  low: 'bg-green-500/20 text-green-400 border-green-500/30'
};

const typeColors = {
  performance: 'from-blue-500/20 to-blue-600/10 border-blue-500/30',
  opportunity: 'from-green-500/20 to-green-600/10 border-green-500/30',
  alert: 'from-amber-500/20 to-amber-600/10 border-amber-500/30',
  creative: 'from-purple-500/20 to-purple-600/10 border-purple-500/30',
  industry_benchmark: 'from-primary/20 to-primary/10 border-primary/30',
  goal: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30',
  challenge: 'from-orange-500/20 to-orange-600/10 border-orange-500/30',
  kpi_tracking: 'from-cyan-500/20 to-cyan-600/10 border-cyan-500/30'
};

export function AIInsightsWidget() {
  const [insights, setInsights] = useState([]);
  const [summary, setSummary] = useState(null);
  const [personalization, setPersonalization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchInsights = async (showToast = false) => {
    try {
      const response = await fetch(`${API_URL}/api/dashboard/ai-insights`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setInsights(data.insights || []);
        setSummary(data.summary);
        setPersonalization(data.personalization);
        if (showToast) {
          toast.success('Insights refreshed');
        }
      }
    } catch (error) {
      console.error('Failed to fetch insights:', error);
      if (showToast) {
        toast.error('Failed to refresh insights');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchInsights(true);
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-slate-700">
        <CardContent className="p-6 flex items-center justify-center h-[300px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
            <p className="text-slate-400 text-sm">Analyzing your campaigns...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-slate-700" data-testid="ai-insights-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="p-1.5 bg-gradient-to-br from-primary to-primary/60 rounded-lg">
              <Sparkles className="w-4 h-4 text-primary-foreground" />
            </div>
            AI Insights
            {personalization?.has_onboarding_data && (
              <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/30">
                Personalized
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="text-slate-400 hover:text-white"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Link to="/ai-chat">
              <Button variant="ghost" size="sm" className="text-primary hover:text-primary/80">
                Chat with AI
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Personalization Context */}
        {personalization?.has_onboarding_data && (
          <div className="mt-2 p-2 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex flex-wrap gap-2 text-xs">
              {personalization.industry && (
                <span className="flex items-center gap-1 text-slate-400">
                  <Building2 className="w-3 h-3" />
                  {personalization.industry}
                </span>
              )}
              {personalization.goals?.length > 0 && (
                <span className="flex items-center gap-1 text-slate-400">
                  <Target className="w-3 h-3" />
                  {personalization.goals[0]}
                </span>
              )}
              {personalization.kpis?.length > 0 && (
                <span className="flex items-center gap-1 text-slate-400">
                  <BarChart3 className="w-3 h-3" />
                  Tracking {personalization.kpis.length} KPIs
                </span>
              )}
            </div>
          </div>
        )}
        
        {summary && (
          <div className="flex gap-3 mt-2">
            <Badge variant="outline" className="bg-slate-700/50 text-slate-300 border-slate-600">
              {summary.total_insights} insights
            </Badge>
            {summary.high_priority > 0 && (
              <Badge variant="outline" className={priorityColors.high}>
                {summary.high_priority} high priority
              </Badge>
            )}
            {summary.personalized_count > 0 && (
              <Badge variant="outline" className="bg-primary/20 text-primary border-primary/30">
                {summary.personalized_count} personalized
              </Badge>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="pt-2">
        <ScrollArea className="h-[220px] pr-2">
          <div className="space-y-3">
            {insights.map((insight, idx) => {
              const Icon = iconMap[insight.icon] || Lightbulb;
              const isPersonalized = insight.personalized;
              return (
                <div
                  key={idx}
                  className={`p-3 rounded-lg bg-gradient-to-r ${typeColors[insight.type] || typeColors.performance} border transition-all hover:scale-[1.01] ${isPersonalized ? 'ring-1 ring-primary/20' : ''}`}
                  data-testid={`insight-${idx}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-slate-800/50 rounded-lg flex-shrink-0">
                      <Icon className="w-4 h-4 text-slate-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-sm text-white">{insight.title}</h4>
                        <Badge 
                          variant="outline" 
                          className={`text-[10px] py-0 ${priorityColors[insight.priority]}`}
                        >
                          {insight.priority}
                        </Badge>
                        {isPersonalized && (
                          <Badge 
                            variant="outline" 
                            className="text-[10px] py-0 bg-primary/10 text-primary border-primary/30"
                          >
                            for you
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mb-1">{insight.message}</p>
                      <p className="text-xs text-slate-300 flex items-center gap-1">
                        <Zap className="w-3 h-3 text-primary" />
                        {insight.recommendation}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
        
        {/* Call to action for users without onboarding */}
        {!personalization?.has_onboarding_data && (
          <div className="mt-3 p-3 bg-primary/10 rounded-lg border border-primary/20 text-center">
            <p className="text-xs text-slate-400 mb-2">Complete your profile for personalized insights</p>
            <Link to="/onboarding">
              <Button size="sm" variant="outline" className="text-xs border-primary/30 text-primary hover:bg-primary/10">
                <Target className="w-3 h-3 mr-1" />
                Set Up Profile
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function AIQuickActions() {
  return (
    <Card className="bg-gradient-to-br from-coral-500/10 to-purple-600/10 border-coral-500/30" data-testid="ai-quick-actions">
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-gradient-to-br from-coral-500 to-purple-600 rounded-lg">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">AI Assistant</h3>
            <p className="text-xs text-slate-400">Get instant help with your campaigns</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Link to="/ai-chat?prompt=analyze">
            <Button variant="outline" size="sm" className="w-full text-xs border-slate-600 hover:bg-slate-700">
              Analyze Performance
            </Button>
          </Link>
          <Link to="/ai-chat?prompt=optimize">
            <Button variant="outline" size="sm" className="w-full text-xs border-slate-600 hover:bg-slate-700">
              Optimize Budget
            </Button>
          </Link>
          <Link to="/ai-chat?prompt=creative">
            <Button variant="outline" size="sm" className="w-full text-xs border-slate-600 hover:bg-slate-700">
              Generate Ad Copy
            </Button>
          </Link>
          <Link to="/ai-chat?prompt=forecast">
            <Button variant="outline" size="sm" className="w-full text-xs border-slate-600 hover:bg-slate-700">
              Forecast Results
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

export default AIInsightsWidget;
